import {
  hexColorSchema,
  settingsSchema,
  type Settings,
  type SettingsResponse,
} from "@go-novus/admin-api";

import * as m from "#/paraglide/messages";

import {
  defaultWaitSeconds,
  PRODUCT_TITLE_TOKEN,
  type ProactiveRuleDraft,
} from "#/features/settings";

/**
 * 草稿和已保存配置同形，只有两处例外：商家在 Hex 框里逐字符输入时，
 * 中间态（`#e46`）不是合法值，所以草稿里存原样文本，提交前才规范化；
 * 规则的等待秒数允许为 null，表示输入框被清空，按必填字段拦下保存。
 */
export type SettingsDraft = Omit<Settings, "proactiveRules"> & {
  proactiveRules: ProactiveRuleDraft[];
};

/** 每条规则一份字段错误；没有键就是这个字段没问题。 */
export interface RuleFieldErrors {
  prompt?: string;
  question?: string;
  /** 选择器问题：启用元素规则却留空目标、或格式被判非法。 */
  selector?: string;
  waitSeconds?: string;
  /** 不属于单个字段的规则级问题（id、触发类型等）。 */
  rule?: string;
}

/** 每个能出错的字段一条文案；规则错误按规则 id 归组。 */
export interface DraftErrors {
  agentName?: string;
  /** proactiveRules 数组本身的问题，如重复 id。 */
  proactiveRules?: string;
  /** 按规则 id 归组的字段错误。 */
  rules?: Record<string, RuleFieldErrors>;
  /** 按行号归组的建议问题错误。 */
  suggestedQuestions?: Record<number, string>;
  supportEmail?: string;
  themeColor?: string;
  welcomeMessage?: string;
}

/** 契约存大写 `#RRGGBB`，商家怎么输都按这个存。 */
export function normalizeHexColor(value: string): string {
  const trimmed = value.trim();
  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  return withHash.toUpperCase();
}

/**
 * 规范化之后仍然合法才给出颜色，否则 null。合法与否的规则仍归契约 schema。
 * 调用方拿它区分「商家还在敲」和「这就是一个颜色」——原生取色控件和预览都只收后者。
 */
export function readHexColor(value: string): string | null {
  const parsed = hexColorSchema.safeParse(normalizeHexColor(value));
  return parsed.success ? parsed.data : null;
}

export function readDraft(saved: SettingsResponse): SettingsDraft {
  return {
    proactiveRules: saved.proactiveRules.map((rule) => ({ ...rule })),
    agent: { ...saved.agent },
    chatbox: { ...saved.chatbox },
    // 只取可编辑的邮箱；验证状态是服务端只读字段，重发后变化不算未保存修改。
    handoff: { supportEmail: saved.handoff.supportEmail },
  };
}

/**
 * 只规范化不影响合法性的部分：Hex 大小写。规则的等待秒数必须保持原样——
 * null 要被 schema 判成错误，而不是被悄悄填回默认值。
 */
function normalizeDraft(draft: SettingsDraft): SettingsDraft {
  return {
    ...draft,
    chatbox: {
      ...draft.chatbox,
      themeColor: normalizeHexColor(draft.chatbox.themeColor),
    },
  };
}

/** 把草稿规范化成可提交的形状；校验仍由契约 schema 做。 */
export function readSubmittableDraft(draft: SettingsDraft): Settings {
  const normalized = normalizeDraft(draft);
  return {
    ...normalized,
    proactiveRules: normalized.proactiveRules.map((rule) => ({
      ...rule,
      // 走到这里草稿已经通过校验，waitSeconds 不会是 null；这一支只为满足类型。
      waitSeconds: rule.waitSeconds ?? defaultWaitSeconds(rule.trigger),
    })),
  };
}

export function isDraftDirty(
  draft: SettingsDraft,
  saved: SettingsResponse,
): boolean {
  return JSON.stringify(draft) !== JSON.stringify(readDraft(saved));
}

/**
 * 校验完全交给契约 schema：合法 Hex、欢迎语 1–80、建议问题每条 1–80 且不重复、Agent 名 1–40、
 * Support email 是邮箱、规则按 GON-62 的字段与模板约束，
 * 规则都写在 settings.ts / proactive-rules.ts 里，这里只把错误摊到字段上。
 */
export function readDraftErrors(draft: SettingsDraft): DraftErrors {
  const parsed = settingsSchema.safeParse(normalizeDraft(draft));

  if (parsed.success) {
    return {};
  }

  const errors: DraftErrors = {};

  for (const issue of parsed.error.issues) {
    const [group, field] = issue.path;

    if (group === "proactiveRules") {
      const rule =
        typeof field === "number" ? draft.proactiveRules[field] : undefined;

      if (rule === undefined) {
        // 数组级问题（重复 id 等），不属于某一条规则。
        errors.proactiveRules = m.settings_error_proactive_rules();
        continue;
      }

      const ruleErrors = (errors.rules ??= {});
      const bucket = (ruleErrors[rule.id] ??= {});
      const ruleField = issue.path[2];

      if (ruleField === "prompt") {
        bucket.prompt = m.settings_error_rule_prompt({
          placeholder: PRODUCT_TITLE_TOKEN,
        });
      } else if (ruleField === "question") {
        bucket.question = m.settings_error_rule_question({
          placeholder: PRODUCT_TITLE_TOKEN,
        });
      } else if (ruleField === "selector") {
        bucket.selector = m.settings_error_rule_selector();
      } else if (ruleField === "waitSeconds") {
        bucket.waitSeconds = m.settings_error_rule_wait();
      } else {
        bucket.rule = m.settings_error_proactive_rules();
      }
      continue;
    }

    if (group === "agent" && field === "name") {
      errors.agentName = m.settings_error_agent_name();
    }

    if (group === "chatbox" && field === "welcomeMessage") {
      errors.welcomeMessage = m.settings_error_welcome_message();
    }

    const row = issue.path[2];

    if (
      group === "chatbox" &&
      field === "suggestedQuestions" &&
      typeof row === "number"
    ) {
      // 两条空行既算空又算重复；字数问题先报出来，重复不覆盖它。
      (errors.suggestedQuestions ??= {})[row] ??=
        issue.code === "custom"
          ? m.settings_error_suggested_question_duplicate()
          : m.settings_error_suggested_question();
    }

    if (group === "chatbox" && field === "themeColor") {
      errors.themeColor = m.settings_error_theme_color();
    }

    if (group === "handoff" && field === "supportEmail") {
      errors.supportEmail = m.settings_error_support_email();
    }
  }

  return errors;
}

export function hasDraftErrors(errors: DraftErrors): boolean {
  return Object.keys(errors).length > 0;
}
